<template>
  <section class="duallo-admin-table">
    <header class="duallo-table-header">
      <div>
        <div class="duallo-eyebrow">Organization</div>
        <h3 class="duallo-table-title">Manage Assets</h3>
      </div>
      <div class="table-controls">
        <v-switch v-model="showInactive" label="Show deleted" color="primary" hide-details inset density="compact" />
      </div>
    </header>

    <div class="duallo-table-card">
      <div v-if="isLoading" class="duallo-state-block">
        <div v-for="n in 3" :key="n" class="duallo-row-skeleton row-skeleton" />
      </div>

      <div v-else-if="filteredAssets.length === 0" class="duallo-state-block empty-state">
        <v-icon size="40" color="#4a5a60">mdi-package-variant-closed</v-icon>
        <p class="empty-title">No assets found</p>
        <p class="empty-subtitle">{{ showInactive ? 'No assets exist for this organization.' : 'Toggle "Show deleted" to see inactive assets.' }}</p>
      </div>

      <template v-else>
        <div v-if="!isCompact" class="duallo-table-wrap">
          <table class="duallo-data-table">
            <thead>
              <tr>
                <th class="col-thumb"></th>
                <th class="col-name">Name</th>
                <th class="col-desc">Description</th>
                <th class="col-actions"></th>
              </tr>
            </thead>
            <tbody>
              <AssetTableItem v-for="asset in filteredAssets" :key="asset.id" :asset="asset" :compact="false"
                @onAssetModified="updateAssets" />
            </tbody>
          </table>
        </div>

        <div v-else class="duallo-card-list">
          <AssetTableItem v-for="asset in filteredAssets" :key="asset.id" :asset="asset" :compact="true"
            @onAssetModified="updateAssets" />
        </div>
      </template>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, defineOptions, onMounted } from 'vue';
import { useDisplay } from 'vuetify';
import { useOrganizationStore } from '@/store/organizationStore'
import AssetService from '@/services/AssetService';
import AssetTableItem from './table-items/AssetTableItem.vue';

defineOptions({ name: 'AssetTable' });

const { smAndDown } = useDisplay();
const isCompact = computed(() => smAndDown.value);

const assets = ref([]);
const organizationStore = useOrganizationStore();
const showInactive = ref(false);
const isLoading = ref(true);

const filteredAssets = computed(() =>
  assets.value.filter(asset => showInactive.value ? true : asset.isActive)
);

const loadAssets = async () => {
  isLoading.value = true;
  try {
    assets.value = await AssetService.getAssetsByOrganizationIdAsync(organizationStore.organizationId);
  } finally {
    isLoading.value = false;
  }
};

const updateAssets = async () => { await loadAssets(); };

onMounted(loadAssets);
</script>

<style scoped>
.table-controls :deep(.v-label) {
  color: #cfd8dc;
  font-size: 0.85rem;
}

/* AssetTable uses a taller skeleton row than the shared 48px default */
.row-skeleton {
  height: 56px;
}

.empty-state {
  align-items: center;
  text-align: center;
  padding: 40px 16px;
  color: #8aa0aa;
}

.empty-title {
  font-size: 1.05rem;
  font-weight: 600;
  color: #cfd8dc;
  margin: 12px 0 4px;
}

.empty-subtitle {
  font-size: 0.88rem;
  margin: 0;
  max-width: 380px;
}

.col-thumb { width: 90px; }
.col-name { min-width: 140px; }
.col-desc { width: auto; }
.col-actions { width: 120px; text-align: right; }
</style>
